import { makeStyles, shorthands, tokens } from "@fluentui/react-components";

export const checkoutStyles = makeStyles({
  container: {
    // Page shell must be able to grow past one viewport: a fixed height clamps
    // the box while content overflows, letting the shared footer overlap it and
    // pushing the order actions below the scrollable area.
    minHeight: "100vh",
    flexDirection: "column",
    justifyContent: "space-between",
    backgroundColor: tokens.colorNeutralBackground3,
    // Prefer the dynamic viewport unit when supported so mobile browser chrome
    // doesn't add a phantom scroll. Falls back transparently to 100vh on
    // Safari < 15.4, Chromium < 108, Firefox < 101.
    "@supports (min-height: 100dvh)": {
      minHeight: "100dvh",
    },
    "@media (min-width: 500px)": {
      height: "90vh",
      // Pin the floor to the same 90vh so the mobile-first minHeight above
      // can't grow the desktop shell past its established height.
      minHeight: "90vh",
      backgroundColor: tokens.colorNeutralBackground2,
    },
  },
  main: {
    ...shorthands.overflow("hidden"),
    "@media (min-width: 500px)": {
      ...shorthands.overflow("hidden"),
      display: "flex",
      flexDirection: "column",
      width: "100%",
      alignItems: "stretch",
      justifyContent: "flex-start",
      height: "100%",
      ...shorthands.padding("24px", "0px", "20px", "0px"),
      ...shorthands.gap("6px"),
    },
  },
  subMainContainer: {
    display: "none",
    "&:hover": {
      overflowY: "auto",
    },
    "@media (min-width: 500px)": {
      display: "flex",
      flexDirection: "row-reverse",
      ...shorthands.padding("0px", "16px", "0px", "0px"),
      ...shorthands.borderRadius("8px"),
      boxShadow: tokens.shadow2,
      height: "calc(90vh - 68px)",
      ...shorthands.overflow("hidden"),
      scrollbarGutter: "stable",
      "::-webkit-scrollbar": {
        width: "4px",
        height: "4px",
      },
      "::-webkit-scrollbar-thumb": {
        backgroundColor: "#888",
        ...shorthands.borderRadius("4px"),
      },
      "::-webkit-scrollbar-track": {
        backgroundColor: "#ccc",
        ...shorthands.borderRadius("4px"),
      },
      "::-webkit-scrollbar-thumb:hover": {
        backgroundColor: "#888",
      },
    },
    "@media (min-width: 500px) and (max-width: 899px)": {
      marginLeft: "40px",
      marginRight: "40px",
      maxWidth: "100%",
      "&:hover": {
        overflowX: "auto",
      },
    },
    "@media (min-width: 900px) and (max-width: 1239px)": {
      ...shorthands.margin("0px", "auto"),
      width: "840px",
    },
    "@media (min-width: 1240px) and (max-width: 1399px)": {
      marginLeft: "200px",
      marginRight: "200px",
      maxWidth: "100%",
    },
    "@media (min-width: 1400px)": {
      ...shorthands.margin("0px", "auto"),
      width: "1040px",
    },
  },
  mainContent: {
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    width: "100%",
    height: "100%",
    overflowY: "hidden",
    overflowX: "hidden",
  },
  yourOrder: {
    width: "100%",
    alignItems: "center",
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.padding("0px", "0px", "12px", "0px"),
  },
  reviewLine: {
    flexDirection: "row",
    width: "100%",
    justifyContent: "space-between",
    ...shorthands.padding("16px"),
  },
  edit: {
    color: tokens.colorPalettePurpleForeground2,
  },
  cartEntriesContainer: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    justifyContent: "space-between",
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.gap("6px"),
    overflowY: "auto",
    overflowX: "hidden",
  },
  cartEntriesHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding("18px"),
  },
  cartEntriesList: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.padding("9px", "0px", "9px", "0px"),
    ...shorthands.gap("9px"),
  },
  orderButtons: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    ...shorthands.gap("12px"),
    ...shorthands.padding("16px"),
    alignItems: "stretch",
    "@media (orientation: landscape)": {
      flexDirection: "column",
    },
    "@media (min-width: 500px)": {
      flexDirection: "column",
      ...shorthands.gap("8px"),
      paddingLeft: "27px",
      paddingRight: "27px",
      flexShrink: 0,
    },
    "& > button": {
      width: "100%",
      minHeight: "48px",
    },
  },
  semiBoldBaseFont: {
    fontFamily: tokens.fontFamilyBase,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase600,
    "@media (min-width: 500px)": {
      fontSize: "14px",
    },
  },
  paddingAndBorderRadius: {
    width: "100%",
    ...shorthands.padding("16px", "8px"),
    ...shorthands.borderRadius("4px"),
    minHeight: "48px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "@media (min-width: 500px)": {
      ...shorthands.padding("12px", "16px"),
      minHeight: "48px",
    },
  },
  cancelButton: {
    ...shorthands.borderColor(tokens.colorBrandForeground1),
    color: tokens.colorBrandForeground1,
    "&:disabled": {
      cursor: "not-allowed",
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      color: tokens.colorNeutralForegroundDisabled,
      ...shorthands.borderColor(tokens.colorTransparentBackground),
      "&:hover": {
        backgroundColor: tokens.colorNeutralBackgroundDisabled,
        color: tokens.colorNeutralForegroundDisabled,
        ...shorthands.borderColor(tokens.colorTransparentBackground),
      },
    },
  },
  orderButton: {
    backgroundColor: tokens.colorBrandForeground1,
    "&:disabled": {
      backgroundColor: tokens.colorNeutralBackgroundDisabled,
      color: tokens.colorNeutralForegroundDisabled,
      "&:hover": {
        backgroundColor: tokens.colorNeutralBackgroundDisabled,
        color: tokens.colorNeutralForegroundDisabled,
      },
    },
    "&:hover": {
      backgroundColor: "#5432E6",
    },
  },
  cartHeaderContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
    backgroundColor: tokens.colorNeutralBackground1,
    width: "100%",
    ...shorthands.padding("28px", "18px", "28px", "18px"),
  },
  cartHeaderInfo: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
  },
  pickupLocation: {
    color: tokens.colorBrandBackgroundStatic,
  },
  cartCafeInfo: {
    fontWeight: tokens.fontWeightRegular,
  },
  dialog: {
    width: "90vw",
  },
  dialogActions: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
    ...shorthands.gap("12px"),
  },
  mobileViewContainer: {
    display: "block",
    "@media (min-width: 500px)": {
      display: "none",
    },
  },
  cartEntries: {
    "@media (min-width: 500px)": {
      display: "flex",
      flexDirection: "column",
      width: "344px",
      minWidth: "344px",
      maxHeight: "100%",
      overflowY: "auto",
      overflowX: "hidden",
    },
  },
  payments: {
    "@media (min-width: 500px)": {
      ...shorthands.flex(1),
      paddingLeft: "16px",
      height: "inherit",
      backgroundColor: tokens.colorNeutralBackground1,
      display: "block",
      ...shorthands.borderRadius("8px"),
      borderTopRightRadius: "0px",
      borderBottomRightRadius: "0px",
    },
  },
});
